import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { masterApi } from "@/contexts/MasterAuthContext";
import { Building2, Plus, Globe, CreditCard, Edit, Trash2, Users } from "lucide-react";

export default function MasterClubs() {
    const [clubs, setClubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = () => {
        setLoading(true);
        masterApi.get("/master/clubs")
            .then((r) => setClubs(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Yüklenemedi"))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const handleDeactivate = async (clubId) => {
        if (!window.confirm("Bu kulübü deaktif etmek istediğinizden emin misiniz?")) return;
        try {
            await masterApi.delete(`/master/clubs/${clubId}`);
            load();
        } catch (e) {
            alert(e?.response?.data?.detail || "İşlem başarısız");
        }
    };

    return (
        <div data-testid="master-clubs">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-yellow-400 mb-2 flex items-center gap-2"><Building2 size={14}/> Kulüpler</div>
                    <h1 className="text-4xl font-extrabold tracking-tight">Tüm Kulüpler ({clubs.length})</h1>
                </div>
                <Link to="/master/onboarding" data-testid="goto-onboarding-btn" className="inline-flex items-center gap-2 px-5 py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold uppercase tracking-wider text-sm rounded-lg transition-all">
                    <Plus size={16} /> Yeni Kulüp
                </Link>
            </div>

            {error && <div className="text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">{error}</div>}
            {loading && <div data-testid="loading">Yükleniyor…</div>}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {clubs.map((c) => {
                    const sub = c.subscription || {};
                    const pct = sub.monthly_credit_limit ? Math.round(((sub.credit_balance || 0) / sub.monthly_credit_limit) * 100) : 0;
                    return (
                        <div key={c.id} data-testid={`club-card-${c.id}`} className="bg-[#121212] border border-[#2a2a2a] hover:border-yellow-500/40 rounded-2xl p-6 transition-all">
                            <div className="flex items-start gap-3 mb-4">
                                <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: c.primary_color || "#f5dc4c", color: c.accent_color || "#000" }}>
                                    {c.logo_url ? <img src={c.logo_url} alt={c.name} className="w-10 h-10 object-contain"/> : <Building2 size={22} />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold truncate" title={c.name}>{c.name}</div>
                                    <div className="text-xs text-neutral-500 mt-0.5">{c.id}</div>
                                </div>
                                <span className={`text-xs px-2 py-1 rounded ${c.is_active ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                                    {c.is_active ? "Aktif" : "Pasif"}
                                </span>
                            </div>

                            <div className="text-sm text-neutral-400 mb-3 line-clamp-2 min-h-[2.5rem]">{c.description || "—"}</div>

                            <div className="space-y-2 mb-4 text-xs">
                                <div className="flex items-center justify-between">
                                    <span className="text-neutral-500 flex items-center gap-1.5"><Globe size={12}/> Domain</span>
                                    <span className="truncate ml-2 max-w-[60%]">{(c.domains || []).join(", ") || "—"}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-neutral-500 flex items-center gap-1.5"><CreditCard size={12}/> Paket</span>
                                    <span className="capitalize font-semibold text-yellow-400">{c.subscription_plan || "starter"}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-neutral-500 flex items-center gap-1.5"><Users size={12}/> Kullanıcı</span>
                                    <span>{c.user_count}</span>
                                </div>
                            </div>

                            <div className="mb-4">
                                <div className="flex items-center justify-between text-xs mb-1.5">
                                    <span className="text-neutral-500">AI Kredisi</span>
                                    <span className="font-mono">{sub.credit_balance || 0} / {sub.monthly_credit_limit || 0}</span>
                                </div>
                                <div className="h-1.5 bg-[#0a0a0a] rounded overflow-hidden">
                                    <div className="h-full bg-yellow-500" style={{ width: `${pct}%` }} />
                                </div>
                            </div>

                            <div className="flex gap-2">
                                <Link to={`/master/clubs/${c.id}`} data-testid={`edit-club-${c.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-[#1a1a1a] hover:bg-[#222] border border-[#2a2a2a] text-sm rounded-lg transition-colors">
                                    <Edit size={13}/> Düzenle
                                </Link>
                                {c.id !== "livanespor" && (
                                    <button onClick={() => handleDeactivate(c.id)} data-testid={`delete-club-${c.id}`} className="px-3 py-2 bg-[#1a1a1a] hover:bg-red-500/20 hover:text-red-400 border border-[#2a2a2a] text-sm rounded-lg transition-colors">
                                        <Trash2 size={13}/>
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
